/**
 * Drawer — reusable right-side panel. Desktop only.
 *
 * Props:
 *   isOpen   — boolean, controls visibility
 *   onClose  — function, called on Close button click, scrim click, or Escape
 *   children — content to render inside the panel
 *
 * Behaviour:
 *   - Desktop (≥1200px): right-anchored frosted panel, 751px, backdrop scrim
 *   - Mobile: does NOT render (consumers navigate to full-page route instead)
 *   - Escape key closes
 *   - Scrim click closes
 *   - Body scroll locked while open
 *
 * Architecture: ADR-029
 */
import { useEffect } from 'react';
import s from './Drawer.module.css';

export default function Drawer({ isOpen, onClose, children }) {
  /* Lock body scroll while open */
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  /* Escape key */
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Scrim */}
      <div
        className={s.scrim}
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Panel */}
      <aside
        className={s.panel}
        role="dialog"
        aria-modal="true"
      >
        {/* Close button — Figma: dark bg, cream text, top-left of panel */}
        <div className={s.closeRow}>
          <button
            type="button"
            className={s.closeButton}
            onClick={onClose}
          >
            Close
          </button>
        </div>
        {/* Scrollable content */}
        <div className={s.body}>
          {children}
        </div>
      </aside>
    </>
  );
}
